import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import Select from "react-select";
import "../electricityprovider/electricityProvider.scss";
import indiaLocations from "../data/indiaLocations.json";
import { api, fetchData } from "../helper/api.interceptor";

const EditProviderModal = ({ show, onClose, providerCode, onProviderUpdated }) => {
    const [form, setForm] = useState({
        name: "",
        code: "",
        state: "",
        cities: [],
        billingCycles: [],
        image: null,
        imagePreview: null,
    });

    const [availableCities, setAvailableCities] = useState([]);
    const [loading, setLoading] = useState(false);

    // ✅ Fetch provider data
    useEffect(() => {
        const fetchProvider = async () => {
            if (!providerCode || !show) return;
            setLoading(true);
            try {
                const response = await api.getElectricityProviderByCode(providerCode);
                if (response) {
                    const data = response;

                    // Get state from API and update available cities accordingly
                    const selectedState = data.state || "";
                    const stateObj = indiaLocations.find((s) => s.state === selectedState);
                    const districtList = stateObj
                        ? stateObj.districts.map((d) => d.district)
                        : [];

                    setAvailableCities(districtList);

                    setForm({
                        name: data.name || "",
                        code: data.code || "",
                        state: selectedState,
                        cities:
                            data.cities?.map((c) => ({
                                label: c,
                                value: c,
                            })) || [],
                        billingCycles:
                            (data.billingCycles || []).map((b) =>
                                typeof b === "string" ? b : b.label
                            ),
                        image: null,
                        imagePreview: data.image
                            ? data.image.replace("localhost", window.location.hostname)
                            : null,
                    });
                }
            } catch (error) {
                Swal.fire("Error", "Failed to load provider data", "error");
            }
            setLoading(false);
        };

        fetchProvider();
    }, [providerCode, show]);

    // ✅ When state changes → load districts
    useEffect(() => {
        if (form.state) {
            const stateObj = indiaLocations.find((s) => s.state === form.state);
            if (stateObj && Array.isArray(stateObj.districts)) {
                setAvailableCities(stateObj.districts.map((d) => d.district));
            } else {
                setAvailableCities([]);
            }

            // ✅ Only clear cities if user manually changes state (not on load)
            if (!form.cities.length) {
                setForm((prev) => ({ ...prev, cities: [] }));
            }
        } else {
            setAvailableCities([]);
        }
    }, [form.state]);

    const handleBillingCycleToggle = (cycle) => {
        setForm((prev) => {
            const updated = prev.billingCycles.includes(cycle)
                ? prev.billingCycles.filter((c) => c !== cycle)
                : [...prev.billingCycles, cycle];
            return { ...prev, billingCycles: updated };
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setForm((prev) => ({
                    ...prev,
                    image: file,
                    imagePreview: reader.result,
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name || !form.code || !form.state || form.billingCycles.length === 0) {
            return Swal.fire("Missing Fields", "Please fill out all fields", "warning");
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("name", form.name);
            formData.append("state", form.state);
            formData.append("cities", JSON.stringify(form.cities.map((c) => c.value)));
            formData.append(
                "billingCycles",
                JSON.stringify(form.billingCycles.map((label) => ({ label })))
            );
            formData.append("regions", JSON.stringify([]));
            formData.append("rates", JSON.stringify([]));

            if (form.image) formData.append("image", form.image);

            const response = await fetchData.post(
                `/update-electricity-providers?code=${form.code}`,
                formData
            );

            if (response?.status_code === 200) {
                Swal.fire("Success", "Electricity provider updated successfully", "success");
                onProviderUpdated();
                onClose();
            } else {
                throw new Error(response?.data?.message || "Failed to update provider");
            }
        } catch (error) {
            Swal.fire("Error", error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal-backdrop">
            <div className="modal-container">
                <form className="modal-content" onSubmit={handleSubmit}>
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Electricity Provider</h5>
                        <button type="button" className="close-btn" onClick={onClose}>
                            <Icon icon="mdi:close" />
                        </button>
                    </div>

                    <div className="modal-body">
                        {/* Provider Name */}
                        <div className="form-group">
                            <label>Provider Name</label>
                            <input
                                type="text"
                                name="name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, name: e.target.value }))
                                }
                                className="form-control"
                                placeholder="Enter provider name"
                                required
                            />
                        </div>

                        {/* Provider Code */}
                        <div className="form-group">
                            <label>Provider Code</label>
                            <input
                                type="text"
                                name="code"
                                value={form.code}
                                readOnly
                                className="form-control"
                            />
                        </div>

                        {/* State dropdown */}
                        <div className="form-group">
                            <label>State</label>
                            <Select
                                options={indiaLocations.map((s) => ({
                                    label: s.state,
                                    value: s.state,
                                }))}
                                value={
                                    form.state
                                        ? { label: form.state, value: form.state }
                                        : null
                                }
                                onChange={(selected) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        state: selected?.value || "",
                                        cities: [],
                                    }))
                                }
                                placeholder="Select State"
                                isClearable
                            />
                        </div>

                        {/* City (district) dropdown */}
                        <div className="form-group">
                            <label>Cities</label>
                            <Select
                                isMulti
                                options={availableCities.map((c) => ({
                                    label: c,
                                    value: c,
                                }))}
                                value={form.cities}
                                onChange={(selected) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        cities: selected || [],
                                    }))
                                }
                                placeholder={
                                    form.state
                                        ? "Select one or more Cities"
                                        : "Select a state first"
                                }
                                isDisabled={!availableCities.length}
                            />
                        </div>

                        {/* Billing Cycle */}
                        <div className="form-group">
                            <label>Billing Cycle</label>
                            <div className="toggle-group">
                                {["1 month", "2 month"].map((cycle) => (
                                    <button
                                        type="button"
                                        key={cycle}
                                        className={`toggle-btn ${form.billingCycles.includes(cycle)
                                            ? "active"
                                            : ""
                                            }`}
                                        onClick={() => handleBillingCycleToggle(cycle)}
                                    >
                                        {cycle}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Image Upload */}
                        <div className="form-group">
                            <label>Provider Logo</label>
                            <div className="file-upload">
                                {form.imagePreview ? (
                                    <div className="image-preview-container">
                                        <img
                                            src={form.imagePreview}
                                            alt="Preview"
                                            className="image-preview"
                                        />
                                        <button
                                            type="button"
                                            className="remove-image-btn"
                                            onClick={() =>
                                                setForm((prev) => ({
                                                    ...prev,
                                                    image: null,
                                                    imagePreview: null,
                                                }))
                                            }
                                        >
                                            <Icon icon="mdi:close" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            id="provider-image-edit"
                                            className="file-input"
                                        />
                                        <label
                                            htmlFor="provider-image-edit"
                                            className="upload-label"
                                        >
                                            <Icon icon="solar:upload-linear" />
                                            <span>Upload Logo</span>
                                        </label>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Icon icon="eos-icons:loading" className="spinner" />
                                    Updating...
                                </>
                            ) : (
                                "Update Provider"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProviderModal;


// import React, { useEffect, useState } from "react";
// import { Icon } from "@iconify/react";
// import Swal from "sweetalert2";
// import "../electricityprovider/electricityProvider.scss";
// import { api, fetchData } from "../helper/api.interceptor";

// const EditProviderModal = ({ show, onClose, providerCode, onProviderUpdated }) => {
//     const [form, setForm] = useState({
//         name: "",
//         code: "",
//         billingCycles: [],
//         image: null,
//         imagePreview: null,
//     });

//     const [loading, setLoading] = useState(false);

//     useEffect(() => {
//         const fetchProvider = async () => {
//             if (!providerCode || !show) return;
//             setLoading(true);
//             try {
//                 const response = await api.getElectricityProviderByCode(providerCode);
//                 if (response) {
//                     const data = response;
//                     setForm({
//                         name: data.name || "",
//                         code: data.code || "",
//                         billingCycles: (data.billingCycles || []).map((b) =>
//                             typeof b === "string" ? b : b.label
//                         ),
//                         image: null,
//                         imagePreview: data.image
//                             ? data.image.replace("localhost", window.location.hostname)
//                             : null,
//                     });
//                 }
//             } catch (error) {
//                 Swal.fire("Error", "Failed to load provider data", "error");
//             }
//             setLoading(false);
//         };

//         fetchProvider();
//     }, [providerCode, show]);

//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setForm({ ...form, [name]: value });
//     };

//     const handleBillingCycleToggle = (cycle) => {
//         setForm((prev) => {
//             const updated = prev.billingCycles.includes(cycle)
//                 ? prev.billingCycles.filter((c) => c !== cycle)
//                 : [...prev.billingCycles, cycle];
//             return { ...prev, billingCycles: updated };
//         });
//     };

//     const handleFileChange = (e) => {
//         const file = e.target.files[0];
//         if (file) {
//             const reader = new FileReader();
//             reader.onloadend = () => {
//                 setForm((prev) => ({
//                     ...prev,
//                     image: file,
//                     imagePreview: reader.result,
//                 }));
//             };
//             reader.readAsDataURL(file);
//         }
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();

//         if (!form.name || !form.code || form.billingCycles.length === 0) {
//             return Swal.fire("Missing Fields", "Please fill out all fields", "warning");
//         }

//         setLoading(true);
//         try {
//             const formData = new FormData();
//             formData.append("name", form.name); // FIRST
//             formData.append(
//                 "billingCycles",
//                 JSON.stringify(form.billingCycles.map((label) => ({ label })))
//             );
//             formData.append("regions", JSON.stringify([]));
//             formData.append("rates", JSON.stringify([]));

//             if (form.image) {
//                 formData.append("image", form.image);
//             }
//             for (const [key, value] of formData.entries()) {
//                 console.log(`${key}:`, value);
//             }

//             const response = await fetchData.post(
//                 `/update-electricity-providers?code=${form.code}`,
//                 formData
//             );

//             if (response?.status_code === 200) {
//                 Swal.fire("Success", "Electricity provider updated successfully", "success");
//                 onProviderUpdated();
//                 onClose();
//             } else {
//                 throw new Error(response?.data?.message || "Failed to update provider");
//             }
//         } catch (error) {
//             Swal.fire("Error", error.message, "error");
//         } finally {
//             setLoading(false);
//         }
//     };

//     if (!show) return null;

//     return (
//         <div className="modal-backdrop">
//             <div className="modal-container">
//                 <form className="modal-content" onSubmit={handleSubmit}>
//                     <div className="modal-header">
//                         <h5 className="modal-title">Edit Electricity Provider</h5>
//                         <button type="button" className="close-btn" onClick={onClose}>
//                             <Icon icon="mdi:close" />
//                         </button>
//                     </div>
//                     <div className="modal-body">
//                         <div className="form-group">
//                             <label>Provider Name</label>
//                             <input
//                                 type="text"
//                                 name="name"
//                                 value={form.name}
//                                 onChange={handleChange}
//                                 className="form-control"
//                                 placeholder="Enter provider name"
//                                 required
//                             />
//                         </div>

//                         <div className="form-group">
//                             <label>Provider Code</label>
//                             <input
//                                 type="text"
//                                 name="code"
//                                 value={form.code}
//                                 onChange={handleChange}
//                                 className="form-control"
//                                 placeholder="e.g. ELEC001"
//                                 readOnly
//                             />
//                         </div>

//                         <div className="form-group">
//                             <label>Billing Cycle</label>
//                             <div className="toggle-group">
//                                 {["1 month", "2 month"].map((cycle) => (
//                                     <button
//                                         type="button"
//                                         key={cycle}
//                                         className={`toggle-btn ${form.billingCycles.includes(cycle) ? "active" : ""}`}
//                                         onClick={() => handleBillingCycleToggle(cycle)}
//                                     >
//                                         {cycle}
//                                     </button>
//                                 ))}
//                             </div>
//                         </div>

//                         <div className="form-group">
//                             <label>Provider Logo</label>
//                             <div className="file-upload">
//                                 {form.imagePreview ? (
//                                     <div className="image-preview-container">
//                                         <img src={form.imagePreview} alt="Preview" className="image-preview" />
//                                         <button
//                                             type="button"
//                                             className="remove-image-btn"
//                                             onClick={() =>
//                                                 setForm((prev) => ({
//                                                     ...prev,
//                                                     image: null,
//                                                     imagePreview: null,
//                                                 }))
//                                             }
//                                         >
//                                             <Icon icon="mdi:close" />
//                                         </button>
//                                     </div>
//                                 ) : (
//                                     <>
//                                         <input
//                                             type="file"
//                                             accept="image/*"
//                                             onChange={handleFileChange}
//                                             id="provider-image-edit"
//                                             className="file-input"
//                                         />
//                                         <label htmlFor="provider-image-edit" className="upload-label">
//                                             <Icon icon="solar:upload-linear" />
//                                             <span>Upload Logo</span>
//                                         </label>
//                                     </>
//                                 )}
//                             </div>
//                         </div>
//                     </div>
//                     <div className="modal-footer">
//                         <button type="button" className="btn btn-secondary" onClick={onClose}>
//                             Cancel
//                         </button>
//                         <button type="submit" className="btn btn-primary" disabled={loading}>
//                             {loading ? (
//                                 <>
//                                     <Icon icon="eos-icons:loading" className="spinner" />
//                                     Updating...
//                                 </>
//                             ) : (
//                                 "Update Provider"
//                             )}
//                         </button>
//                     </div>
//                 </form>
//             </div>
//         </div>
//     );
// };

// export default EditProviderModal;
